import { Dropdown, Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import { Link } from 'react-router-dom'
import { getMeetingLists } from '../board-members/core/_requests'
import { convertTo12HourFormat, toTitleCase } from '../../compliance-meeting/core/_util'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React from 'react'
import NoDataPage from '../../widgets/components/NoDataPage'
import SkeletonRow from '../../digital-signing/components/SkeletonRow'
import { DeleteMeetingConfirmationModal } from './../../compliance-meeting/components/CancelConfimrationModal'
import { deleteParticipants, updateMeetingParticipant } from '../../compliance-meeting/core/_requests'
import { toast } from 'react-toastify'
import { useStakeholders } from '../Stakeholders'
import { DeleteFounderPromoterModal } from '../founders-and-promoters/components/DeleteFounderPromoterModal'

export const ComplianceMeetingStakeholderTable = ({
  email,
  globalFilter,
  setGlobalFilter,
}: {
  email: string | undefined
  globalFilter: string
  setGlobalFilter: Dispatch<SetStateAction<string>>
}) => {
  const { formatMessage } = useIntl()
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [loading, setLoading] = useState(false)
  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({ itemRank })
    return itemRank.passed
  }
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [meetingsData, setMeetingsData] = useState<any[]>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [participantId, setParticipantId] = useState<string | null>(null)
  const { selectedComplianceMeetingTypeFilter, selectedComplianceMeetingDateFilter } =
    useStakeholders()
  const [showDeleteModal, setDeleteShowModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [participantStatus, setParticipantStatus] = useState<'absent' | 'present' | null>('absent')
  const [meetingId, setMeetingId] = useState<string | null>(null)
  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })
  const multiFieldGlobalFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const nameMatch = rankItem(`${row.original.meetingTitle}`, value)
    // Determine which match to use based on rank comparison or other logic
    let bestMatch: RankingInfo | undefined
    if (nameMatch.passed) {
      bestMatch = nameMatch
    }

    // Only call addMeta if a best match is found
    if (bestMatch) {
      addMeta({ itemRank: bestMatch })
    }

    // Return whether the search term passed in either field
    return !!bestMatch
  }

  const updateParticipants = async () => {
    if (!participantId || !meetingId || !participantStatus) return
    setDeleteLoading(true)
    try {
      const {
        data: { success },
      } = await updateMeetingParticipant({ status: participantStatus }, meetingId, participantId)
      if (success) {
        getMeetingList()
        toast.success('Status updated successfully')
      }
    } catch (err) {
      console.log(err)
    } finally {
      setDeleteLoading(false)
      setDeleteShowModal(false)
    }
  }

  const getMeetingList = async () => {
    if (!email) return
    try {
      setLoading(true)
      const {
        data: { success, data },
      } = await getMeetingLists(
        'all',
        pagination.currentPage,
        pagination.pageSize,
        email,
        selectedComplianceMeetingTypeFilter,
        selectedComplianceMeetingDateFilter
      )
      if (success) {
        setMeetingsData(data.data.meetingList)
        setPagination((prev) => ({
          ...prev,
          currentPage: data.pagination?.currentPage,
          pageSize: data.pagination?.pageSize,
          totalCount: data.pagination?.totalRecords,
          nextPage: data.pagination?.nextPage,
          prevPage: data.pagination?.prevPage,
          totalPages: data.pagination?.totalPages,
        }))
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const removeParticipant = async (data: any) => {
    if (!participantId) return
    setCancelLoading(true)
    try {
      const {
        data: { success },
      } = await deleteParticipants(participantId)
      if (success) {
        getMeetingList()
        toast.success(formatMessage({ id: 'Participant removed successfully.' }))
      }
    } catch (err) {
      console.log(err)
    } finally {
      setCancelLoading(false)
      setShowCancelModal(false)
    }
  }

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorKey: 'meetingTitle',
        id: 'MeetingTitle',
        header: 'Meeting Details',
        cell: (info) => {
          const { meetingTitle, meetingDate, startTime, meetingParticipants } = info.row.original
          const attendance = email
            ? meetingParticipants.find((data: any) => data.email === email)?.status
            : 'Unknown' // Destructured for readability
          return (
            <div className='d-flex align-items-center'>
              <div
                style={{
                  borderLeft: `3px solid ${attendance === 'upcoming'
                    ? 'yellow'
                    : attendance === 'present'
                      ? 'green'
                      : attendance === 'invited'
                        ? 'blue'
                        : attendance === 'accept'
                          ? 'green'
                          : 'red'
                    }`,
                }}
              >
                <div className='ps-2'>
                  <div className='font-weight-500 font-size-16'>{meetingTitle}</div>
                  <div>
                    {meetingDate} {` |  ${convertTo12HourFormat(startTime)}`}
                  </div>
                </div>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'participants',
        id: 'Participants',
        header: 'Participants',
        cell: (info) => {
          const { meetingParticipants } = info.row.original
          return <div className='px-3'>{meetingParticipants.length}</div>
        },
      },
      {
        accessorKey: 'Organizer',
        id: 'Organizer',
        header: 'Organizer',
        cell: (info) => {
          const { meetingParticipants } = info.row.original
          const name = meetingParticipants.find((data: any) => data.type === 'Organizer')?.name
          return <p className='mb-4 font-size-16 font-weight-500'>{name}</p>
        },
      },
      {
        accessorKey: 'status',
        id: 'Status',
        header: 'Status',
        cell: (info) => {
          const { meetingParticipants, schedule } = info.row.original
          const attendance =
            email && meetingParticipants.find((data: any) => data.email === email)?.status
          return (
            <div
              className={`badge py-2 px-6 w-100px ${attendance === 'upcoming'
                ? 'alert-warning'
                : attendance === 'present'
                  ? 'alert-success'
                  : attendance === 'invited'
                    ? 'alert-primary'
                    : attendance === 'accept'
                      ? 'alert-success'
                      : 'alert-danger'
                }`}
            >
              <span className='flex-root'>{schedule ? toTitleCase(attendance) : 'Draft'}</span>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const { meetingId } = row.original
          return (
            <div className='dropdown-container d-flex justify-content-center'>
              <Dropdown
                show={meetingId === selectedIndex}
                onToggle={(isOpen, event) => {
                  if (event.source === 'click') {
                    setSelectedIndex(isOpen ? meetingId : undefined)
                  } else {
                    setSelectedIndex(undefined)
                  }
                }}
              >
                <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                  <img src={threeDots} alt='' className='three-dots-image' />
                </Dropdown.Toggle>
                <Dropdown.Menu className='m-2'>
                  <Dropdown.Item className='text-primary p-0'>
                    <Link
                      to={`/compliance-meeting/view/after-meeting?meetingId=${meetingId}`}
                      className='text-dark'
                    >
                      <p className='mb-0 ps-5 py-3'>{formatMessage({ id: 'View' })}</p>
                    </Link>
                  </Dropdown.Item>
                  {!(
                    row?.original?.meetingParticipants?.find(
                      (participant: any) => participant.email === email
                    ).status === 'present' ||
                    row?.original?.meetingParticipants?.find(
                      (participant: any) => participant.email === email
                    ).status === 'absent' ||
                    row?.original?.meetingParticipants?.find(
                      (participant: any) => participant.email === email
                    ).status === 'reject'
                  ) && (
                      <>
                        <Dropdown.Item
                          className='text-primary p-0'
                          onClick={() => {
                            setParticipantStatus('present')
                            setMeetingId(row?.original?.meetingId)
                            setParticipantId(
                              row?.original?.meetingParticipants?.find(
                                (participant: any) => participant.email === email
                              ).meetingParticipantId
                            )
                            setDeleteShowModal(true)
                          }}
                        >
                          <p className='mb-0 ps-5 py-3'>{formatMessage({ id: 'Mark as Present' })}</p>
                        </Dropdown.Item>
                        <Dropdown.Item
                          className='text-danger p-0'
                          onClick={() => {
                            setParticipantStatus('absent')
                            setMeetingId(row?.original?.meetingId)
                            setParticipantId(
                              row?.original?.meetingParticipants?.find(
                                (participant: any) => participant.email === email
                              ).meetingParticipantId
                            )
                            setDeleteShowModal(true)
                          }}
                        >
                          <p className='mb-0 ps-5 py-3'>{formatMessage({ id: 'Mark as Absent' })}</p>
                        </Dropdown.Item>
                      </>
                    )}

                  {row?.original?.meetingParticipants?.find(
                    (participant: any) => participant.type === 'Organizer'
                  ) && (
                      <Dropdown.Item
                        className='text-danger py-0 ps-0'
                        onClick={() => {
                          setParticipantId(
                            row?.original?.meetingParticipants?.find(
                              (participant: any) => participant.email === email
                            ).meetingParticipantId
                          )
                          setShowCancelModal(true)
                        }}
                      >
                        <p className='mb-0 ps-5 py-3'>
                          {formatMessage({ id: 'Remove this Participant' })}
                        </p>
                      </Dropdown.Item>
                    )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )
        },
      },
    ],
    [selectedIndex] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const table = useReactTable({
    data: meetingsData,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: true,
    rowCount: pagination.totalCount,
    state: {
      columnFilters,
      globalFilter: globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: multiFieldGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  useEffect(() => {
    getMeetingList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    pagination.currentPage,
    pagination.pageSize,
    selectedComplianceMeetingTypeFilter,
    selectedComplianceMeetingDateFilter,
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }

  return (
    <>
      <div className='bg-white rounded-2 p-4'>
        {!loading && (meetingsData?.length === 0 || table.getRowModel().rows.length === 0) ? (
          <div className='my-5 mx-6'>
            <NoDataPage width={'w-0'} />
          </div>
        ) : (
          <Table className='w-100 '>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 w-100 table-row border-dark-mode '
                >
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan} className=' pb-6 pt-4'>
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading || !email ? (
                <>
                  <SkeletonRow count={8} />
                </>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className='border-bottom'>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className='pt-4 border-0'>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
        {!loading && meetingsData?.length > 0 && pagination.totalCount > 10 && (
          <div className='w-100 d-flex justify-content-between align-items-center px-0 mt-8'>
            <div className='d-flex px-0 gap-2 w-100 align-items-center justify-content-start mb-4'>
              <select
                aria-label='Default select example'
                className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13'
                value={pagination.pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value)
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: newSize,
                    pageIndex: 0, // Reset to first page when page size changes
                  }))
                }}
              >
                {[10, 20, 50, 100].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>

              <span className='font-size-13 text-center text-md-start'>
                {formatMessage({
                  id: `Showing ${pagination.currentPage === 1
                    ? 1
                    : (pagination.currentPage - 1) * pagination.pageSize + 1
                    } to ${pagination.totalCount > pagination.currentPage * pagination.pageSize
                      ? pagination.currentPage * pagination.pageSize
                      : pagination.totalCount -
                      pagination.currentPage * pagination.pageSize +
                      pagination.currentPage * pagination.pageSize
                    } of ${pagination.totalCount} records`,
                })}
              </span>
            </div>

            {/* <Pagination className='w-md-100 w-auto d-flex flex-end px-0'>
              <Pagination.First onClick={() => handlePageChange(1)} disabled={!pagination.prevPage}>
                {'<<'}
              </Pagination.First>

              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.prevPage}
              >
                {'<'}
              </Pagination.Prev>

              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <Pagination.Item
                  key={i}
                  active={i === pagination.currentPage - 1}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.nextPage}
              >
                {'>'}
              </Pagination.Next>

              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.nextPage}
              >
                {'>>'}
              </Pagination.Last>
            </Pagination> */}
            <Pagination className='w-md-100 w-auto d-flex flex-end px-0'>
              {/* First Page Button */}
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={pagination.currentPage === 1}
              >
                {'<<'}
              </Pagination.First>

              {/* Previous Page Button */}
              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                {'<'}
              </Pagination.Prev>

              {/* Page Number Buttons */}
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <Pagination.Item
                  key={i}
                  active={i === pagination.currentPage - 1}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}

              {/* Next Page Button */}
              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages}
              >
                {'>'}
              </Pagination.Next>

              {/* Last Page Button */}
              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.currentPage >= pagination.totalPages}
              >
                {'>>'}
              </Pagination.Last>
            </Pagination>

          </div>
        )}
      </div>
      <DeleteMeetingConfirmationModal
        setDeleteShowModal={setShowCancelModal}
        showDeleteModal={showCancelModal}
        onDeleteSubmit={removeParticipant}
        deleteLoading={cancelLoading}
        isParticipant={true}
      />
      <DeleteFounderPromoterModal
        setDeleteShowModal={setDeleteShowModal}
        showDeleteModal={showDeleteModal}
        onDeleteSubmit={updateParticipants}
        deleteLoading={deleteLoading}
        header={`Mark Participant as ${participantStatus ? toTitleCase(participantStatus) : 'Present'
          }`}
        paragraph={`Are you sure you want to mark this participant as ${participantStatus}?`}
        isParticipant={true}
        participantHeading={participantStatus}
      />
    </>
  )
}
